import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Spacer from "../../Spacer";
import RespiratoryRate from "../../icons/RespiratoryRate";
import {
  isSpirometryFailure,
  pickSpirometryResult,
} from "../../../utils/results/spirometry";
import { formatValidatedMeasurement } from "../../../utils/results/validatedRanges";

function getSpiroQualityMessage({ signalQuality, zeroedMetrics }, t) {
  if (!isSpirometryFailure({ signalQuality, zeroedMetrics })) return null;

  return {
    title: t("result.spiro.qualityFailTitle", "Low signal quality."),
    subtitle: t("result.spiro.qualityFailSubtitle", "Please try again."),
  };
}

function SpirometryResultViewImpl({ data, onFinish }) {
  const { t } = useTranslation();

  const { fev1, pef, signalQuality, zeroedMetrics } = useMemo(
    () => pickSpirometryResult(data),
    [data],
  );

  const qualityMsg = useMemo(
    () => getSpiroQualityMessage({ signalQuality, zeroedMetrics }, t),
    [signalQuality, zeroedMetrics, t],
  );
  const fev1Display = useMemo(
    () => formatValidatedMeasurement("fev1", fev1),
    [fev1],
  );
  const pefDisplay = useMemo(
    () => formatValidatedMeasurement("pef", pef),
    [pef],
  );

  return (
    <div className="w-full max-w-lg h-full min-h-0 overflow-y-auto">
      <section className="min-h-full p-4 flex flex-col items-center text-center gap-4">
        <h1 className="subtitle-text">
          {t("result.spiro.completeTitle", "Spirometry scan complete")}
        </h1>

        <div className="flex flex-row items-center gap-6">
          <div
            className={[
              "w-14 h-14 flex items-center justify-center",
              qualityMsg ? "fill-greyscale-3" : "text-primary fill-primary",
            ].join(" ")}
          >
            <RespiratoryRate className="w-full h-full" />
          </div>

          <div className="flex flex-col items-center">
            <div className="flex flex-col items-center justify-center">
              <div className="">{t("result.spiro.fev1", "FEV1")}</div>
              <div
                className={[
                  "supertitle-text",
                  qualityMsg ? "!text-greyscale-3" : "text-primary",
                ].join(" ")}
              >
                {fev1Display}
                <span className="muted-text">
                  {" "}
                  {t("result.spiro.FEV1Unit", "L")}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-center">
            <div className="flex flex-col items-center justify-center">
              <div className="">{t("result.spiro.pef", "PEF")}</div>
              <div
                className={[
                  "supertitle-text",
                  qualityMsg ? "!text-greyscale-3" : "text-primary",
                ].join(" ")}
              >
                {pefDisplay}
                <span className="muted-text">
                  {t("result.spiro.PEFUnit", "L/min")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {qualityMsg ? (
          <div className="flex flex-col items-center gap-1">
            <div className="muted-text">{qualityMsg.title}</div>
            <div className="muted-text">{qualityMsg.subtitle}</div>
          </div>
        ) : null}

        <Spacer />

        <div className="mt-auto w-full pb-2">
          <button
            type="button"
            className="primary-button w-full"
            onClick={onFinish}
          >
            {t("result.finish", "Finish")}
          </button>
        </div>
      </section>
    </div>
  );
}

const SpirometryResultView = memo(SpirometryResultViewImpl);

// Ready when we have at least one metric value or an explicit failure state.
SpirometryResultView.isReady = (data) => {
  const { fev1, pef, signalQuality, zeroedMetrics } = pickSpirometryResult(data);
  return (
    fev1 != null ||
    pef != null ||
    isSpirometryFailure({ signalQuality, zeroedMetrics })
  );
};

export default SpirometryResultView;
